import { useWeb3React } from '@web3-react/core';
import useModalState from '../../../context/modal/useModalState';
import useErrorState from '../../../context/error/useErrorState';
import { signMessage, verifyChainConfig, orderBookQuery } from '../../../helpers';
import {
    ORDER_SUBMIT_PENDING,
    OrderBookActionMapper,
    OrderBookPath,
    OrderBookType,
    SIGNATURE_PENDING,
} from '../../../config/constants';
import { generatePath } from 'react-router-dom';
import useUserPositions from './useUserPositions';
import useToastState from '../../../context/toast/useToastState';
import useUserOrders from './useUserOrders';

const useCancelOrder = () => {
    const { account, chainId, library } = useWeb3React();
    const { openModal, setModalData, closeModal } = useModalState();
    const { setError } = useErrorState();
    const { updateUserOrders } = useUserOrders();
    const { updateUserPositions } = useUserPositions();
    const { setToast } = useToastState();

    const cancel = async (
        orderId: string,
        orderType: OrderBookType,
        callback?: () => void
    ) => {
        if (!account) return;
        verifyChainConfig(chainId);

        try {
            openModal('waitingModal');
            setModalData((prev) => ({ ...prev, status: SIGNATURE_PENDING }));

            const signature = await signMessage(library, account, orderId);

            setModalData((prev) => ({ ...prev, status: ORDER_SUBMIT_PENDING }));

            const path = generatePath(OrderBookPath.CANCEL_ORDER);

            await orderBookQuery(
                path,
                {
                    signature,
                    maker: account.toLowerCase(),
                    orderId: orderId,
                },
                {
                    chainId,
                }
            );

            await updateUserOrders();
            await updateUserPositions();

            callback && callback();

            setToast(
                {
                    type: 'dark',
                    message: (
                        <>
                            {orderType === OrderBookType.OPEN
                                ? `Limit Order`
                                : `${OrderBookActionMapper[orderType]} Limit`}{' '}
                            Cancelled!
                        </>
                    ),
                },
                orderId
            );
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return cancel;
};

export default useCancelOrder;
